import React, { useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import TagSelector from '../TagSelector';

export interface ReceiptItem {
  item_number: string | null;
  description: string | null;
  quantity: number | null;
  unit_price: number | null;
  total_price: number | null;
  vat_rate: number | null;
}

export interface VatBreakdown {
  vat_rate: number;
  tax_base_amount: number;
  vat_amount: number;
}

export interface ReceiptData {
  merchant_name: string | null;
  merchant_address: string | null;
  buyer_name: string | null;
  buyer_address: string | null;
  transaction_date: string | null;
  due_date: string | null;
  total_amount: number | null;
  tax_amount: number | null;
  currency: string;
  client_reference: string | null;
  iban: string | null;
  items: ReceiptItem[];
  vat_breakdown: VatBreakdown[];
}

const getEPCData = ({ name, iban, amount, remittanceInformation }: {
  name: string; iban: string; amount: number; remittanceInformation: string;
}) => {
  const cleanIban = iban.replace(/[^A-Z0-9]/g, '');
  return `BCD\n002\n1\nSCT\n\n${name}\n${cleanIban}\nEUR${amount.toFixed(2)}\n\n\n${remittanceInformation}\n`;
};

interface Props {
  data: ReceiptData;
  tags: string[];
  availableTags: string[];
  onChange: (updated: ReceiptData) => void;
  onTagsChange: (tags: string[]) => void;
  headerTitle?: string;
  headerColor?: string;
  footer: React.ReactNode;
}

const ReceiptDataPanel: React.FC<Props> = ({
  data, tags, availableTags, onChange, onTagsChange,
  headerTitle = 'Extrahierte Daten',
  headerColor = 'bg-blue-600',
  footer,
}) => {
  const [qrPayload, setQrPayload] = useState('');

  useEffect(() => {
    if (data.iban && data.total_amount && data.merchant_name) {
      try {
        setQrPayload(getEPCData({
          name: data.merchant_name,
          iban: data.iban,
          amount: data.total_amount,
          remittanceInformation: data.client_reference || 'Rechnung',
        }));
      } catch { setQrPayload(''); }
    } else {
      setQrPayload('');
    }
  }, [data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    onChange({
      ...data,
      [name]: (name === 'total_amount' || name === 'tax_amount')
        ? parseFloat(value.replace(',', '.')) || 0
        : value,
    });
  };

  const handleVatRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newBreakdown = [...(data.vat_breakdown || [{ vat_rate: 0, tax_base_amount: 0, vat_amount: 0 }])];
    newBreakdown[0] = { ...newBreakdown[0], vat_rate: parseFloat(e.target.value) || 0 };
    onChange({ ...data, vat_breakdown: newBreakdown });
  };

  const standardRates = [0, 5, 7, 16, 19];
  const vatRate = data.vat_breakdown?.[0]?.vat_rate;
  const vatWarning = vatRate !== undefined && vatRate !== null && !standardRates.includes(vatRate);

  return (
    <div className="w-full bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
      <div className={`${headerColor} px-6 py-4`}>
        <h2 className="text-white text-xl font-semibold">{headerTitle}</h2>
      </div>
      <div className="p-6 space-y-6">

        {/* Kopfdaten */}
        <div className="space-y-3">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Kopfdaten & Adressen</h3>
          <FieldRow label="Händler" name="merchant_name" value={data.merchant_name || ''} onChange={handleChange} />
          <FieldRow label="Händler Adresse" name="merchant_address" value={data.merchant_address || ''} onChange={handleChange} isTextArea />
          <FieldRow label="Empfänger" name="buyer_name" value={data.buyer_name || ''} onChange={handleChange} />
          <FieldRow label="Empfänger Adresse" name="buyer_address" value={data.buyer_address || ''} onChange={handleChange} isTextArea />
          <div className="grid grid-cols-2 gap-4">
            <FieldRow label="Belegdatum" name="transaction_date" value={data.transaction_date || ''} onChange={handleChange} />
            <FieldRow label="Fälligkeit" name="due_date" value={data.due_date || ''} onChange={handleChange} />
          </div>
          <FieldRow label="IBAN" name="iban" value={data.iban || ''} onChange={handleChange} />
          <FieldRow label="Referenz" name="client_reference" value={data.client_reference || ''} onChange={handleChange} />
        </div>

        {/* Positionen */}
        {data.items && data.items.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Positionen</h3>
            <div className="border rounded-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Beschr.</th>
                    <th className="px-3 py-2 text-right">Menge</th>
                    <th className="px-3 py-2 text-right">Einzel</th>
                    <th className="px-3 py-2 text-right">Gesamt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {data.items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2">{item.description}</td>
                      <td className="px-3 py-2 text-right">{item.quantity}</td>
                      <td className="px-3 py-2 text-right">{item.unit_price?.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right font-medium">{item.total_price?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Zahlungsdetails */}
        <div className="space-y-3 pt-4 border-t">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Zahlungsdetails</h3>
          <div className="grid grid-cols-2 gap-4">
            <FieldRow label="Steuer" name="tax_amount" value={data.tax_amount?.toFixed(2) || '0.00'} onChange={handleChange} />
            <FieldRow label="Gesamt Brutto" name="total_amount" value={`${(data.total_amount || 0).toFixed(2)} ${data.currency}`} onChange={handleChange} />
          </div>
          <div className="grid grid-cols-3 gap-4 items-center border-b pb-2 border-gray-100">
            <label className="text-sm font-semibold text-gray-500">MwSt-Satz (%)</label>
            <div className="col-span-2 flex items-center gap-2">
              <input
                type="number"
                value={vatRate ?? ''}
                onChange={handleVatRateChange}
                className={`flex-1 p-1 border rounded text-sm font-medium outline-none focus:ring-2 focus:ring-blue-500 ${vatWarning ? 'bg-red-50 border-red-500 text-red-700' : 'bg-gray-50 border-gray-200'}`}
              />
              {vatWarning && (
                <span className="text-[10px] bg-red-600 text-white px-1.5 py-0.5 rounded uppercase font-bold animate-pulse">Prüfen!</span>
              )}
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="space-y-2 pt-4 border-t">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Tags</h3>
          <TagSelector
            selectedTags={tags}
            onChange={onTagsChange}
            availableTags={availableTags}
            placeholder="Tags hinzufügen..."
          />
        </div>
      </div>

      {/* QR Code */}
      {qrPayload && (
        <div className="flex flex-col items-center bg-gray-50 border-t border-gray-100 p-6">
          <p className="text-sm font-semibold text-gray-600 mb-3">Mit Banking-App scannen</p>
          <div className="p-2 bg-white border rounded-lg shadow-sm">
            <QRCodeCanvas value={qrPayload} size={192} />
          </div>
        </div>
      )}

      <div className="bg-gray-100 px-6 py-4 flex justify-end gap-3">
        {footer}
      </div>
    </div>
  );
};

const FieldRow = ({ label, name, value, onChange, isTextArea }: {
  label: string; name: string; value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  isTextArea?: boolean;
}) => (
  <div className="grid grid-cols-3 gap-4 items-center border-b border-gray-100 pb-2">
    <label htmlFor={name} className="font-medium text-gray-600 col-span-1 text-sm">{label}</label>
    {isTextArea ? (
      <textarea id={name} name={name} value={value} onChange={onChange} rows={2}
        className="col-span-2 text-gray-900 font-semibold bg-gray-50 border border-gray-200 rounded-md px-3 py-1 focus:ring-blue-500 focus:border-blue-500 text-sm" />
    ) : (
      <input type="text" id={name} name={name} value={value} onChange={onChange}
        className="col-span-2 text-gray-900 font-semibold bg-gray-50 border border-gray-200 rounded-md px-3 py-1 focus:ring-blue-500 focus:border-blue-500" />
    )}
  </div>
);

export default ReceiptDataPanel;
